/**
 * BillingHistory Component
 *
 * Displays token transaction history with type filtering and pagination.
 * Shows in the Settings modal under the Billing tab (serverless mode only).
 */

import { useState, useMemo } from 'react';
import { Coins, ArrowDownCircle, Gift, Calendar, RotateCcw, Loader2, Receipt } from 'lucide-react';
import { useTokenHistory, type TokenTransaction } from '../../hooks/useTokenHistory';
import { useTranslation } from '../../hooks/useTranslation';
import { publicAsset } from '../../utils/assetUrl';

// ============================================================================
// Types
// ============================================================================

type FilterType = 'all' | 'usage' | 'purchase' | 'bonus' | 'monthly' | 'refund';

interface FilterOption {
  value: FilterType;
  labelKey: string;
}

// ============================================================================
// Constants
// ============================================================================

const FILTER_OPTIONS: FilterOption[] = [
  { value: 'all', labelKey: 'settings.billing.filterAll' },
  { value: 'usage', labelKey: 'settings.billing.filterUsage' },
  { value: 'purchase', labelKey: 'settings.billing.filterPurchase' },
  { value: 'bonus', labelKey: 'settings.billing.filterBonus' },
  { value: 'monthly', labelKey: 'settings.billing.filterMonthly' },
  { value: 'refund', labelKey: 'settings.billing.filterRefund' },
];

// ============================================================================
// Helpers
// ============================================================================

function getTransactionIcon(type: string): React.ReactNode {
  switch (type) {
    case 'usage':
      return <ArrowDownCircle className="h-4 w-4 text-red-400" />;
    case 'purchase':
      return <Coins className="h-4 w-4 text-green-400" />;
    case 'bonus':
      return <Gift className="h-4 w-4 text-purple-400" />;
    case 'monthly':
      return <Calendar className="h-4 w-4 text-blue-400" />;
    case 'refund':
      return <RotateCcw className="h-4 w-4 text-yellow-400" />;
    default:
      return <Receipt className="h-4 w-4 text-white/50" />;
  }
}

function isDeduction(type: string): boolean {
  return type === 'usage' || type === 'expiration';
}

function formatRelativeTime(timestamp: string): string {
  const now = Date.now();
  const then = new Date(timestamp).getTime();
  const diffMs = now - then;

  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;

  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

// ============================================================================
// Transaction Row
// ============================================================================

function TransactionRow({
  tx,
  t,
}: {
  tx: TokenTransaction;
  t: ReturnType<typeof useTranslation>['t'];
}) {
  const deduction = isDeduction(tx.type);
  const amountLabel = deduction
    ? t('settings.billing.tokensDeducted', { defaultValue: 'tokens deducted' })
    : t('settings.billing.tokensAdded', { defaultValue: 'tokens added' });

  return (
    <div className="flex items-center gap-3 p-3 rounded-lg bg-black/20 border border-white/5">
      {/* Icon */}
      <div className="flex-shrink-0">{getTransactionIcon(tx.type)}</div>

      {/* Description + time */}
      <div className="flex-1 min-w-0">
        <p className="text-sm text-white/90 truncate">{tx.description}</p>
        <p className="text-xs text-white/40">{formatRelativeTime(tx.timestamp)}</p>
      </div>

      {/* Amount badge */}
      <div className="flex-shrink-0 text-right">
        <span
          className={`text-sm font-medium inline-flex items-center gap-1 ${deduction ? 'text-red-400' : 'text-green-400'}`}
        >
          {deduction ? '-' : '+'}
          {Math.abs(tx.amount).toLocaleString()}
          <img
            src={publicAsset('token-coin-32.png')}
            alt=""
            className="h-3.5 w-3.5"
            draggable={false}
          />
        </span>
        <p className="text-xs text-white/40">{amountLabel}</p>
      </div>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function BillingHistory() {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<FilterType>('all');

  const transactionType = filter === 'all' ? null : filter;
  const { transactions, loading, error, loadMore, hasMore } = useTokenHistory({
    limit: 20,
    transactionType,
  });

  // Memoize filter options with translated labels
  const filterButtons = useMemo(
    () =>
      FILTER_OPTIONS.map((opt) => ({
        ...opt,
        label: t(opt.labelKey, {
          defaultValue: opt.value.charAt(0).toUpperCase() + opt.value.slice(1),
        }),
      })),
    [t]
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <h3 className="text-sm font-medium text-white mb-1 flex items-center gap-2">
          <Receipt className="h-4 w-4" />
          {t('settings.billing.title', { defaultValue: 'Token History' })}
        </h3>
        <p className="text-xs text-white/50">
          {t('settings.billing.description', {
            defaultValue: 'View your token usage and transaction history.',
          })}
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-1.5">
        {filterButtons.map((opt) => (
          <button
            key={opt.value}
            onClick={() => setFilter(opt.value)}
            className={`px-3 py-1.5 text-xs rounded-full transition-colors ${
              filter === opt.value
                ? 'bg-purple-600 text-white'
                : 'bg-black/20 text-white/60 hover:text-white/80 border border-white/10'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Loading */}
      {loading && transactions.length === 0 && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-white/40" />
        </div>
      )}

      {/* Error */}
      {error && <div className="text-center py-6 text-sm text-red-400">{error}</div>}

      {/* Transactions list */}
      {!loading && !error && transactions.length === 0 && (
        <div className="text-center py-8 text-sm text-white/40">
          {t('settings.billing.noTransactions', { defaultValue: 'No transactions yet' })}
        </div>
      )}

      {transactions.length > 0 && (
        <div className="space-y-2">
          {transactions.map((tx, idx) => (
            <TransactionRow key={`${tx.timestamp}-${idx}`} tx={tx} t={t} />
          ))}
        </div>
      )}

      {/* Load more */}
      {hasMore && transactions.length > 0 && (
        <button
          onClick={loadMore}
          disabled={loading}
          className="w-full py-2 text-sm text-white/60 hover:text-white/80 bg-black/20 rounded-lg border border-white/10 transition-colors disabled:opacity-50"
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin mx-auto" />
          ) : (
            t('settings.billing.loadMore', { defaultValue: 'Load more' })
          )}
        </button>
      )}
    </div>
  );
}
